import { useContext, useEffect, useRef, useState } from 'react';

import axios from 'axios';

import { Greeting } from '../types';

import { useHistory, useParams } from 'react-router-dom';
import { AppContext } from '~/contexts/AppContext';

import RatingEnd from './topic/RatingEnd';

import styled from 'styled-components';
import QuestionAndAnswer from './topic/QuestionAndAnswer';
import StyledContentPanel from '../components/StyledContentPanel';
import TypingTextV2 from '../components/TypingTextV2';
import SliderPanel from './topic/SliderPanel';
import { animated, useSpring } from 'react-spring';
import { useTranslation } from 'react-i18next';
import { capitalizeFirst, fixBold } from '../util/util';

import { Helmet } from 'react-helmet';
import Greetings from './topic/Greetings';
import OptionsButton from '../components/OptionsButton';
import Fade from '../components/Fade';

import MatomoTracker from '../util/matomo/MatomoTracker';
import ConsentOverlay from '../components/ConsentOverlay';
import RatingConfirmationDialog from '../components/RatingConfirmationDialog';
import SendButton from '../components/SendButton';

import { availableLanguages } from '../i18n/languages';

import * as topicUtils from '../util/topic';

// razmera na ikonite 28px
const RatingScreenContainer = styled(animated.div)`
    width: 100%; 
    display: flex;
    flex-direction: column; 
    transition: height 1s ease;
    
    ${(props: { topicstate: TopicState, showresults: string }) => props.topicstate === TopicState.Initial ? `
        height: 100%;
    ` : props.showresults === 'true' ? `
    
        height: auto;
        min-height: 400px;
    ` : `
        height: auto;
        margin-bottom: -10px;
    `}
`;

export enum RatingState {
    Initial = 'initial',
    Touched = 'touched',
    Moved = 'moved',
}

enum TopicState {
    Loading = 'loading',
    Greeting = 'greeting',
    Initial = 'initial',
    SliderTransform = 'slider-transform',
    RatingResults = 'rating-result',
    QnA = 'qna',
    EndScreen = 'rating-end'
}

enum TopicAnimationState {
    ContentPanelFadeIn = 'content-panel-fade-in',
    TypeTitleText = 'type-title-text',
    TypeSubTitleText = 'type-subtitle-text',
    SliderFadeIn = 'slider-fade-in',
    RatingConfirmation = 'rating-confirmation',
    SubTitleReverse = 'subtitle-reverse',
    SubTitleTypeRating = 'subtitle-type-rating',
    TitleAndSliderFadeOut = 'title-and-slider-fade-out'
}

interface TopicScreenState {
    sliderActive: boolean;
    topicState: TopicState;
    topicAnimationState: TopicAnimationState;
    ratingValue: number | null;
    unconfirmedRatingValue: number | null;
    ratingId: number | null;
    showConsentOverlay: boolean;
    firstRating: boolean;
}

const initState = {
    sliderActive: false,
    topicState: TopicState.Loading,
    topicAnimationState: TopicAnimationState.ContentPanelFadeIn,
    ratingValue: null,
    unconfirmedRatingValue: null,
    ratingId: null,
    showConsentOverlay: false,
    firstRating: false
};

const TopicComponent: React.FC = () => {

    const {
        apiUrl, topicData, profile, setProfile, showResults,
        setTopicData, setGlassPanelColor, setGlassPanelBackdropFilter, setLoading,
        revertSlider, triggerConfetti
    } = useContext(AppContext)!;

    const { topicHash }: { topicHash: string } = useParams();
    const history = useHistory();

    const { i18n, t } = useTranslation();

    const [state, setState] = useState<TopicScreenState>(initState);

    const stateRef = useRef<TopicScreenState>(state);
    useEffect(() => { stateRef.current = state; }, [state]);

    const [ratingScreenStyle, ratingScreenApi] = useSpring(() => ({
        opacity: 1,
        config: { duration: 300 },
    }));

    const [titlePanelStyle, titlePanelApi] = useSpring(() => ({
        opacity: 0,
        config: { duration: 600 },
    }));

    const [sliderPanelStyle, sliderPanelApi] = useSpring(() => ({
        opacity: 0,
        flexGrow: 1,
        config: { duration: 600 },
    }));

    useEffect(() => {
        setGlassPanelColor('');
    }, []);

    const shouldHaveGreetingMessage = (greeting?: Greeting) => {
        if (!greeting) return false;
        return greeting.messages.length > 0 || !!greeting.mediaUrl;
    };

    const getTopic = async (topicHash: string) => {

        const res = await axios.get(`${apiUrl}/topic/${topicHash}`);

        if (!profile && res.data.cookieExists) {
            try {
                const res = await axios.get(`${apiUrl}/user`);
                if(res.data.profile) {
                    setProfile(res.data.profile);
                    window._paq.push(['setUserId', res.data.profile.userId]);
                }
            } catch (error) {
                console.error(error);
            }
        }

        if (res.data.topic === null) {
            history.push('/');
            return;
        }

        // analytics call
        MatomoTracker.trackPageView({
            documentTitle: res.data.topic.name,
            href: `https://kazva.bg/${topicHash}`
        });

        const topicData = res.data;
        const topic = topicData.topic;

        if (topic.disabled) {
            history.push('/');
            return;
        }

        setTopicData(res.data);

        if (!topic.backgroundUrl) {
            setLoading(false);
        }

        if (topic?.enforceLanguage) {
            i18n.changeLanguage(topic.enforceLanguage);
        } else if (topic) {

            const altLngs = topicData.translations ? Object.keys(topicData.translations) : [];
            const topicLanguages = [topic?.defaultLanguage, ...altLngs];
            const currentLanguage = i18n.language;
            const supportedLanguages = Object.keys(availableLanguages);

            // if topic does not provide your system language
            if (!topicLanguages.includes(currentLanguage)) {
                if (!topicLanguages.includes('en') &&
                    supportedLanguages.includes(topic.defaultLanguage)
                ) {
                    // topic does not provide english, but the app provides the topic default
                    i18n.changeLanguage(topic.defaultLanguage);
                } else {
                    // otherwise use english
                    i18n.changeLanguage('en');
                }
            } else {
                i18n.changeLanguage(currentLanguage);
            }
        }

        window.history.pushState(null, '', `/${topicHash}`);

        const shouldBeInGreetingState = shouldHaveGreetingMessage(topicData?.greeting);
        stateRef.current = { ...initState,//...stateRef.current,
            topicState: shouldBeInGreetingState
            ? TopicState.Greeting
            : TopicState.Initial
        };
        setState(stateRef.current);

        ratingScreenApi({ opacity: 1, immediate: true });
        titlePanelApi({ opacity: 0, immediate: true });
        sliderPanelApi({ opacity: 0, immediate: true });

        setTimeout(() => {
            if (!shouldBeInGreetingState)
                startAnimations();
        }, 1000);
    };

    const animationsStartedRef = useRef<boolean>(false);

    /*useEffect(() => {
        if (stateRef.current.topicState === TopicState.Initial && !animationsStartedRef.current) {
            animationsStartedRef.current = true;
            startAnimations();
        }
    }, []);*/

    const startAnimations = () => {
        titlePanelApi.start({
            from: { opacity: 0 },
            opacity: 1,
            onRest: () => {
                stateRef.current = { ...stateRef.current, topicAnimationState: TopicAnimationState.TypeTitleText };
                setState(stateRef.current);
            }
        });
    };

    const resetRating = () => {

        // analytics call
        MatomoTracker.trackPageView({
            documentTitle: topicData?.topic.name,
            href: `https://kazva.bg/${topicHash}`
        });

        const shouldBeInGreetingState = shouldHaveGreetingMessage(topicData?.greeting);
        stateRef.current = { ...initState, topicState: shouldBeInGreetingState ? TopicState.Greeting : TopicState.Initial };
        setState(stateRef.current);

        ratingScreenApi({ opacity: 1, immediate: true });
        sliderPanelApi({ opacity: 0, immediate: true });
        setGlassPanelColor('');

        titlePanelApi.start({
            from: { opacity: 0 },
            opacity: 1,
            onRest: () => {
                stateRef.current = { ...stateRef.current, topicAnimationState: TopicAnimationState.TypeTitleText };
                setState(stateRef.current);
            }
        });
    };

    const selectNextTopic = async (nextTopicSlug: string) => {
        setLoading(true);
        stateRef.current = { ...stateRef.current, topicState: TopicState.Loading };
        setState(stateRef.current);
        setGlassPanelColor('');
        setGlassPanelBackdropFilter(0, 1, 1);

        setTimeout(async () => {
            await getTopic(nextTopicSlug);
            setLoading(false);
        }, 800);
    };

    useEffect(() => {
        getTopic(topicHash);
    }, []);

    const submitRatingValue = (rating: number) => {
        if (state.firstRating) {
            stateRef.current = {
                ...stateRef.current,
                topicAnimationState: TopicAnimationState.RatingConfirmation,
                unconfirmedRatingValue: rating
            };
            setState(stateRef.current);
            triggerConfetti();
        } else {
            stateRef.current = {
                ...stateRef.current,
                topicState: TopicState.SliderTransform,
                topicAnimationState: TopicAnimationState.SubTitleReverse,
                ratingValue: rating
            };
            setState(stateRef.current);
        }

    };

    const setRatingId = (ratingId: number) => {
        stateRef.current = { ...stateRef.current, ratingId };
        setState(stateRef.current);
    };

    const firstQuestionAnswered = () => {
        ratingScreenApi.start({
            opacity: 0,
            onRest: () => {
                stateRef.current = { ...stateRef.current, topicState: TopicState.QnA };
                setState(stateRef.current);
            }
        });
    };

    const showRatingScreen =
        state.topicState === TopicState.Initial ||
        state.topicState === TopicState.RatingResults ||
        state.topicState === TopicState.SliderTransform;

    const showQnA =
        state.topicState === TopicState.RatingResults ||
        state.topicState === TopicState.QnA;

    const showEndScreen = state.topicState === TopicState.EndScreen;
    const ratingConfirmation = state.topicAnimationState === TopicAnimationState.RatingConfirmation;

    useEffect(() => {
        if (state.topicState === TopicState.SliderTransform && !showResults) {
            stateRef.current = { ...stateRef.current, topicState: TopicState.RatingResults };
            setState(stateRef.current);
        }
    }, [state.topicState]);

    const initialQuestion = topicUtils.getInitQuestion(topicData, i18n.language);

    const greetingComplete = () => {
        stateRef.current = { ...stateRef.current, topicState: TopicState.Initial };
        setState(stateRef.current);

        titlePanelApi.start({
            from: { opacity: 0 },
            opacity: 1,
            onRest: () => {
                stateRef.current = { ...stateRef.current, topicAnimationState: TopicAnimationState.TypeTitleText }
                setState(stateRef.current);
            }
        });
    };

    const titleTypingComplete = () => {
        stateRef.current = { ...stateRef.current, topicAnimationState: TopicAnimationState.TypeSubTitleText };
        setState(stateRef.current);
    };

    const subtitleTypingComplete = () => {
        if (stateRef.current.topicAnimationState === TopicAnimationState.TypeSubTitleText) {
            setGlassPanelColor('');
            setGlassPanelBackdropFilter(40, 1, 1);

            stateRef.current = {
                ...stateRef.current,
                showConsentOverlay: !profile,
                sliderActive: !!profile
            };
            setState(stateRef.current);

            sliderPanelApi.start({
                opacity: 1,
                onRest: () => {
                    stateRef.current = {
                        ...stateRef.current,
                        topicAnimationState: TopicAnimationState.SliderFadeIn,
                    };
                    setState(stateRef.current);
                }
            })
        } else if (stateRef.current.topicAnimationState === TopicAnimationState.SubTitleReverse) {
            stateRef.current = { ...stateRef.current, topicAnimationState: TopicAnimationState.SubTitleTypeRating };
            setState(stateRef.current);
        }
    };

    const handleConfirmRating = () => {
        stateRef.current = {
            ...stateRef.current,
            topicState: TopicState.SliderTransform,
            topicAnimationState: TopicAnimationState.SubTitleReverse,
            ratingValue: stateRef.current.unconfirmedRatingValue,
            firstRating: false
        };

        setState(stateRef.current);
    };

    const handleTryAgain = () => {
        stateRef.current = {
            ...stateRef.current,
            topicAnimationState: TopicAnimationState.SliderFadeIn,
            topicState: TopicState.Initial,
            firstRating: false
        }

        if (revertSlider) {
            setGlassPanelColor('');
            setGlassPanelBackdropFilter(40, 1, 1);
            revertSlider();
        }

        setState(stateRef.current);
    };

    const onConsentGiven = async () => {
        //Set Cookie
        const res = await axios.post(`${apiUrl}/user`);
        stateRef.current = {
            ...stateRef.current,
            showConsentOverlay: false,
            sliderActive: true,
            firstRating: true
        };
        setState(stateRef.current);
        setProfile(res.data.profile);
        window._paq.push(['setUserId', res.data.profile.userId]);
    };

    return (
        <>
            {!ratingConfirmation ? null :
                <RatingConfirmationDialog handleConfirmRating={handleConfirmRating} handleTryAgain={handleTryAgain}/>
            }
            {!state.showConsentOverlay ? null :
                <ConsentOverlay topic={topicData?.topic} onConsentGiven={onConsentGiven}/>
            }
            {!topicData ? null :
                state.topicState === TopicState.Greeting
                    ? <Greetings greeting={topicData.greeting} onComplete={greetingComplete} /> :
                    <>
                        <Helmet>
                            <title>{initialQuestion} - Казва БГ</title>
                            <link rel="canonical" href={`https://kazva.bg/${topicHash}`} />
                            <meta name="description" content={'Бъди чут със системата за обратна връзка на България - kazva.bg!'} />
                        </Helmet>
                        {!showRatingScreen ? null :
                            <RatingScreenContainer topicstate={state.topicState} style={ratingScreenStyle} showresults={showResults.toString()}>
                                <animated.div style={{ padding: '20px', paddingBottom: '10px', ...titlePanelStyle }}>
                                    <StyledContentPanel>
                                        <TypingTextV2
                                            delay={10}
                                            active={state.topicAnimationState === TopicAnimationState.TypeTitleText}
                                            onComplete={titleTypingComplete}
                                        >
                                            <h1>{initialQuestion}</h1>
                                        </TypingTextV2>
                                        <TypingTextV2 active={
                                            state.topicAnimationState === TopicAnimationState.TypeSubTitleText ||
                                            state.topicAnimationState === TopicAnimationState.SubTitleReverse ||
                                            state.topicAnimationState === TopicAnimationState.SubTitleTypeRating
                                        }
                                            reverse={state.topicAnimationState === TopicAnimationState.SubTitleReverse}
                                            delay={10}
                                            onComplete={subtitleTypingComplete}
                                        >
                                            {stateRef.current.topicAnimationState === TopicAnimationState.SubTitleTypeRating
                                                ? <p className='auxiliary' style={{ marginTop: '10px' }}>
                                                    {fixBold(t('slider_subtitle_rating_value', { ratingValue: (state.ratingValue ?? 0) / 10 }))}
                                                </p>
                                                : <p className='auxiliary' style={{ marginTop: '10px' }}>{t('slider_subtitle')}</p>
                                            }
                                        </TypingTextV2>
                                    </StyledContentPanel>
                                </animated.div>
                                {state.topicState === TopicState.RatingResults && !showResults ? null :
                                    <animated.div style={{ position: 'relative', ...sliderPanelStyle }}>
                                        <SliderPanel
                                            sliderActive={state.sliderActive}
                                            ratingValue={state.ratingValue}
                                            ratingId={state.ratingId}
                                            submitRatingValue={submitRatingValue}
                                            setRatingId={setRatingId}
                                            onAnimationComplete={() => {
                                                stateRef.current = { ...stateRef.current, topicState: TopicState.RatingResults };
                                                setState(stateRef.current);
                                            }}
                                            firstRating={state.firstRating}
                                        />
                                    </animated.div>
                                }
                            </RatingScreenContainer>
                        }
                        {!showQnA ? null :
                            topicData.qna.questions.length === 0 ?
                                <div style={{ padding: '20px', }}>
                                    <Fade cascade>
                                        <SendButton onClick={() => {
                                            stateRef.current = { ...stateRef.current, topicState: TopicState.EndScreen };
                                            setState(stateRef.current);
                                        }}/>
                                        <OptionsButton />
                                    </Fade>
                                </div> :
                                <div style={{
                                    padding: '20px',
                                    minHeight: state.topicState === TopicState.QnA ? '100%' : 'auto',
                                    position: 'relative',
                                    paddingBottom: '100px'
                                }}>
                                    <QuestionAndAnswer
                                        ratingValue={state.ratingValue}
                                        ratingId={state.ratingId}
                                        firstQuestionAnswered={firstQuestionAnswered}
                                        setRatingEnd={() => {
                                            stateRef.current = { ...stateRef.current, topicState: TopicState.EndScreen };
                                            setState(stateRef.current);
                                        }}
                                    />
                                    <Fade>
                                        <p style={{
                                            margin: 0,
                                            fontSize: '14px',
                                            color: '#fff',
                                            width: 'calc(100% - 140px)',
                                            position: 'absolute',
                                            left: '30px',
                                            bottom: '30px',
                                            paddingLeft: '10px',
                                            paddingTop: '10px',
                                            lineHeight: 1.4
                                        }}>
                                            <b>kazva.bg</b> - {t('footer_title1')}<br />{t('footer_title2')}
                                            <br />
                                            {t('footer_learn_more')}
                                            {' '}
                                            <a style={{
                                                fontWeight: 'bold',
                                                color: 'white',
                                                textDecoration: 'underline'
                                            }}
                                                href="https://cnts.bg" target="_blank"
                                            >
                                                {t('footer_here')}
                                            </a>
                                        </p>
                                        <OptionsButton topic={topicData.topic} />
                                    </Fade>
                                </div>
                        }
                        {!showEndScreen ? null :
                            <div style={{ padding: '20px' }}>
                                <RatingEnd
                                    resetRating={resetRating}
                                    selectNextTopic={selectNextTopic}
                                />
                            </div>
                        }
                    </>}
        </>
    );
};

export default TopicComponent;